import { Box, Portal } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import type { MouseEvent } from "react";

import { colors, ColorIcon, customEvent, LargeButton, LARGEL_BUTTON_TYPE } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

import { ParlorDetailProps } from "@/types/parlor";

const MotionBox = motion(Box);
const backdropVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } };
const sheetVariants = { hidden: { y: "100%" }, visible: { y: 0 }, exit: { y: "100%" } };

/**
 * 목록에 쓸 짧은 주소. 시도명을 뗀다.
 *
 * 시트가 이미 한 지역으로 필터돼 있고 헤더에도 지역명이 있어("부산 금정구 장례식장 8곳")
 * 주소에 시도를 또 쓰면 줄만 길어진다. 시군구까지 떼지 않는 이유는 세종처럼 시군구가 없는
 * 주소에서 도로명이 잘리기 때문이다.
 */
const formatShortAddress = (address?: string): string => {
  if (!address) return "";
  const parts = address.trim().split(/\s+/);
  return parts.length > 1 ? parts.slice(1).join(" ") : address;
};

interface RegionCostFrameParlorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** 폼에서 고른 지역의 장례식장. 페이지 지역이 아니다 */
  parlors: ParlorDetailProps[];
  /** 지금 고른 장례식장 이름들 */
  selectedNames: string[];
  /**
   * 한 곳을 켜고 끈다. 바뀐 **배열 전체**가 아니라 토글할 이름만 넘기는 이유는,
   * 배열을 넘기면 클릭 시점 props 로 계산하게 돼 빠르게 두 번 누를 때 앞 선택이 덮인다.
   * 최신값은 부모의 함수형 setState 가 갖는다.
   */
  onToggle: (name: string) => void;
  /** 헤더에 쓸 지역명. 폼에서 고른 값 */
  regionLabel: string;
  copy: {
    sheetTitle: string;
    sheetHint: string;
    sheetDone: string;
    sheetCount: (count: number) => string;
    pickerEmpty: string;
  };
}

/**
 * C안 장례식장 선택 바텀시트. 여러 곳을 골라 비교할 수 있다.
 *
 * 구조는 `ParlorVisitedListLeadSheet`(장례식장 상세의 방문 목록 시트)와 같다. 다른 점은
 * 대상이 "내가 방문한 목록" 이 아니라 **폼에서 고른 지역의 장례식장 전체**라는 것이다.
 *
 * 선택은 이름(`companyname`)으로 다룬다. id 가 아닌 이유는 이 값이 가는 곳이 로그인 화면의
 * 되짚는 줄과 상담 memo 라 사람이 읽는 문자열이기 때문이다. 견적 body 에는 실리지 않는다
 * (`regionParlorPickContext` 참고).
 */
export default function RegionCostFrameParlorSheet({
  isOpen,
  onClose,
  parlors,
  selectedNames,
  onToggle,
  regionLabel,
  copy,
}: RegionCostFrameParlorSheetProps) {
  /** 시트가 열려 있는 동안 뒤 페이지가 같이 스크롤되지 않게 한다 */
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const toggle = (name: string) => {
    customEvent({
      newGtm: { path: "parlor_region", trigger_and_target: "prlr_pick-toggle" },
    });
    onToggle(name);
  };

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            key="region-parlor-backdrop"
            position="fixed"
            inset={0}
            zIndex={1500}
            bg="rgba(0,0,0,0.5)"
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            alignItems="center"
            onClick={onClose}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <MotionBox
              key="region-parlor-sheet"
              onClick={(e: MouseEvent) => e.stopPropagation()}
              width="100%"
              maxWidth="480px"
              bg={colors.white}
              borderTopLeftRadius="20px"
              borderTopRightRadius="20px"
              padding="18px 16px"
              paddingBottom="calc(16px + env(safe-area-inset-bottom, 0px))"
              boxShadow="0 -8px 32px rgba(0,0,0,0.12)"
              maxHeight="86%"
              display="flex"
              flexDirection="column"
              variants={sheetVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ type: "spring", damping: 30, stiffness: 340, mass: 0.8 }}
            >
              <Box
                width="36px"
                height="4px"
                borderRadius="9999px"
                background={baseColors.line_normal}
                margin="0 auto 14px"
              />

              <BaseStack
                direction="horizontal"
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                spacing={12}
              >
                <Box className="headline_2_b" color={baseColors.label_normal}>
                  {copy.sheetTitle}
                </Box>
                <Box
                  cursor="pointer"
                  flex="none"
                  onClick={onClose}
                  aria-label="닫기"
                  display="flex"
                  alignItems="center"
                  gap="10px"
                >
                  <Box className="label_2_r" color={baseColors.label_assistive_2}>
                    {copy.sheetHint}
                  </Box>
                  <ColorIcon name="close" width={24} height={24} color={colors.gray900} />
                </Box>
              </BaseStack>

              <Box className="label_2_r" color={baseColors.label_alternative_1} marginTop="10px">
                {regionLabel} 장례식장 {parlors.length}곳
              </Box>

              <BaseStack
                width="100%"
                spacing={8}
                marginTop={12}
                overflowY="auto"
                css={{ flex: 1, minHeight: 0 }}
              >
                {parlors.length === 0 ? (
                  <Box
                    className="body_2_normal_r"
                    color={baseColors.label_assistive_2}
                    textAlign="center"
                    padding="28px 0"
                  >
                    {copy.pickerEmpty}
                  </Box>
                ) : (
                  parlors.map((parlor) => {
                    const name = parlor.companyname;
                    const on = selectedNames.includes(name);
                    const binsoCount = parlor.binsos?.length ?? 0;
                    const shortAddress = formatShortAddress(parlor.address);
                    const imageUrl = parlor.images?.[0]?.url;

                    return (
                      <Box
                        key={parlor.id}
                        as="button"
                        type="button"
                        width="100%"
                        textAlign="left"
                        display="flex"
                        alignItems="center"
                        gap="12px"
                        padding="12px"
                        borderRadius="12px"
                        border={`1px solid ${
                          on ? baseColors.label_normal : baseColors.line_normal
                        }`}
                        background={on ? baseColors.fill_normal : colors.white}
                        cursor="pointer"
                        onClick={() => toggle(name)}
                      >
                        <Box
                          flexShrink={0}
                          width="18px"
                          height="18px"
                          borderRadius="5px"
                          border={`1px solid ${on ? colors.orange650 : baseColors.line_normal}`}
                          background={on ? colors.orange650 : colors.white}
                          color={colors.white}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          className="caption_2_b"
                        >
                          {on ? "✓" : ""}
                        </Box>
                        {/*
                         * 시설 사진. 없는 장례식장이 있어 자리만 잡는 회색 박스로 폴백한다.
                         * 줄마다 높이가 달라지면 목록이 들쭉날쭉해 고르기 어려워진다.
                         */}
                        <Box
                          flexShrink={0}
                          width="56px"
                          height="56px"
                          borderRadius="8px"
                          overflow="hidden"
                          background={baseColors.fill_normal}
                        >
                          {imageUrl && (
                            <img
                              src={imageUrl}
                              alt=""
                              loading="lazy"
                              width={56}
                              height={56}
                              style={{ width: "56px", height: "56px", objectFit: "cover" }}
                            />
                          )}
                        </Box>

                        <Box flex={1} wordBreak="keep-all" minWidth={0}>
                          <Box className="body_1_normal_sb" color={baseColors.label_normal}>
                            {name}
                          </Box>
                          {shortAddress && (
                            <Box
                              className="label_2_r"
                              color={baseColors.label_assistive_2}
                              marginTop="3px"
                            >
                              {shortAddress}
                            </Box>
                          )}
                          {binsoCount > 0 && (
                            <Box
                              className="label_2_m"
                              color={baseColors.label_alternative_1}
                              marginTop="2px"
                            >
                              빈소 {binsoCount}개
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </BaseStack>

              <BaseStack
                direction="horizontal"
                width="100%"
                alignItems="center"
                spacing={12}
                marginTop={14}
              >
                <Box className="body_2_normal_sb" color={colors.orange650} flexShrink={0}>
                  {copy.sheetCount(selectedNames.length)}
                </Box>
                <Box flex={1}>
                  <LargeButton
                    className="subtitle_16_sb"
                    height={52}
                    borderRadius={10}
                    buttonType={LARGEL_BUTTON_TYPE.GRAY900}
                    onClick={onClose}
                  >
                    {copy.sheetDone}
                  </LargeButton>
                </Box>
              </BaseStack>
            </MotionBox>
          </MotionBox>
        )}
      </AnimatePresence>
    </Portal>
  );
}
